import { Divider, Stack, Typography, useTheme } from "@mui/material";

import { Strings } from "@app/constants";
import { RHFTextField, SearchBar } from "@app/components";
import { sellCarDummyData } from "@app/modules/sellCar/sellCarDummyData";
import { Icon } from "@iconify/react";
import { useRequestState } from "@app/hooks";
import { selectBrandState } from "@app/modules/categories/selector";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { brand } from "@app/modules/categories/slice";

const Brand = ({ methods, formValue }: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");

  const { data } = useRequestState({
    stateSelector: selectBrandState,
  });

  useEffect(() => {
    dispatch(brand());
  }, []);

  const filteredBrands = data?.filter((item: any) => {
    if (searchTerm === "") return true;
    return item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase());
  });

  const brandId = methods.watch("brandId");
  useEffect(() => {
    if (formValue?.carNumber !== "") {
      methods.setValue("carNumber", formValue?.carNumber);
    }
    if (formValue?.brandId) {
      methods.setValue("brandId", formValue?.brandId);
    }
  }, [formValue, methods, formValue]);

  return (
    <>
      <Stack flexDirection={"column"} py={2}>
        <Icon
          icon="mdi:arrow-left"
          width={20}
          color={theme.palette.common.black}
        />
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 22, sm: 24 },
              fontWeight: "bold",
            }}
          >
            {Strings.step.brand.enterYourCarNumber}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.grey[500], textAlign: "center" }}
          >
            {Strings.step.brand.pleaseProvideYourVehicle}
          </Typography>
          <RHFTextField
            name={"carNumber"}
            placeholder={Strings.step.brand.egDL34AC4564}
            inputProps={{
              style: { textTransform: "uppercase" },
            }}
            sx={{
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: 1,
              backgroundColor: theme.palette.grey[100],
              mt: { xs: 2, md: 5 },
              width: { xs: "100%", sm: "45%" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
                backgroundColor: "transparent",
                boxShadow: "none",
              },
              "& .MuiInputBase-input": {
                padding: "12px 14px",
              },
            }}
          />
        </Stack>
        <Divider sx={{ height: 20 }} />
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mt: { xs: 2, sm: 5 },
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {Strings.step.brand.selectYourCarBrand}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: theme.palette.grey[500] }}
          >
            {Strings.step.brand.chooseFromOurList}
          </Typography>
          <SearchBar setSearchTerm={setSearchTerm} searchTerm={searchTerm} />
        </Stack>
      </Stack>
      <Stack
        sx={{
          mt: { xs: 2, sm: 5 },
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 3,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {filteredBrands?.map((item: any, index: number) => {
          const isSelected = brandId === item?.id;
          return (
            <Stack
              key={index}
              onClick={() =>
                methods.setValue("brandId", item?.id, { shouldValidate: true })
              }
              sx={{
                width: 140,
                height: 160,
                border: "2px solid",
                borderColor: isSelected
                  ? theme.palette.primary.main
                  : theme.palette.grey[300],
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: isSelected
                  ? theme.palette.primary.light
                  : theme.palette.grey[50],
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <Stack
                sx={{
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  border: `1px solid ${theme.palette.grey[300]}`,
                  mb: 1,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <img
                  loading="lazy"
                  src={item?.image}
                  alt={item?.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Stack>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: "center",
                  color: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                }}
              >
                {item?.name}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      {methods.formState.errors?.brandId && (
        <Typography color="error" textAlign="center" mt={2}>
          {methods.formState.errors.brandId.message}
        </Typography>
      )}
    </>
  );
};

export { Brand };
